import { put, call, select, takeLatest } from 'redux-saga/effects';
import {
  EXPORT_GRAPH,
  EXPORT_NETWORK_MAP,
  GET_MOST_CONNECTED,
  GET_TOP_CONNECTIONS,
  SET_PEER_VIEW,
  SET_RESET_VIEW,
  SET_SEARCH_CONTEXT,
  SET_VIEW,
  GET_RELATIONSHIP_SUGGESTIONS,
  CONFIRM_RELATIONSHIP_SUGGESTION,
} from './constants';
import { EXECUTE_FILTER_SEARCH } from '../core/constants';
import { selectCancel, selectConnection, selectUsername } from 'store/core/selectors';
import { createQuery, readBlob } from 'utils/query-helper';
import { getItems } from 'utils/graph-helper';
import { currencyFormatter } from 'utils/formatters/currency';
import { nodeType } from 'utils/constants';
import {
  removeGraphItem,
  setGraphItem,
  setIsLoadingGraphItems,
  setMostConnected,
  setMostConnectedLoading,
  setSearch,
  setTopConnections,
  setTopConnectionsLoading,
  setRelationshipSuggestions,
  setRelationshipSuggestionsLoading,
  dismissRelationshipSuggestion,
  setConfirmationResult,
} from './actions';
import { cancelSearch, setError } from 'store/core/actions';
import { clearNodeFilters } from 'store/filters/actions';
import { SET_NL_MESSAGE } from 'store/nl/actions';
import { nlQuerySaga } from 'store/nl/nl-saga';
import { selectContexts, selectContextIds, selectSelectedVertexId, selectNodes } from './selectors';
import { selectDegreesOfSeparation } from 'store/filters/selectors';
import { getErrorMessage } from 'utils/error-helper';
import { isConnectionReady } from 'utils/connection-helper';

const topConnectionsQuery = async (connection, topConnectionsContext) => {
  if (isConnectionReady(connection)) {
    if (topConnectionsContext && topConnectionsContext.id !== undefined) {
      const response = await connection.invoke(
        'topConnections',
        topConnectionsContext,
      );
      let items = [];
      for (const key in response) {
        const node = response[key];
        items.push({
          ...node,
          key,
        });
      }
      return items;
    }
  }
  return null;
};

const mostConnectedQuery = async (connection, context) => {
  if (isConnectionReady(connection)) {
    if (context && context.id !== undefined) {
      const response = await connection.invoke('mostConnections', context);
      let items = [];
      for (const key in response) {
        const node = response[key];
        items.push({
          ...node,
          key,
        });
      }
      return items;
    }
  }
  return null;
};

const query = async (connection, searchContext, refresh = false) => {
  if (isConnectionReady(connection)) {
    // custom view sets the id for region search.
    // need to undo it when not in custom view for region search
    const isGeoForQuery = !!searchContext.geoType || searchContext.label === 'region';
    if (isGeoForQuery) {
      searchContext.id = '';
    }

    if (searchContext && searchContext.id !== undefined) {
      let traversals = 3;
      if (searchContext.id === '') {
        traversals = 2;
      }
      const query = createQuery([searchContext], [], traversals);
      const blobUrl = await connection.invoke('query', query, refresh);
      const response = await readBlob(blobUrl);
      if (response == null) {
        return null;
      }
      const items = getItems(response, searchContext.id);
      // Attach blob URL for favorites persistence
      return { ...items, blobUrl };
    }
  }
  return null;
};

const queryView = async (connection, view, searchContext, refresh = false) => {
  if (isConnectionReady(connection)) {
    if (searchContext && searchContext.id !== undefined) {
      let traversals = 3;
      if (searchContext.id === '') {
        traversals = 2;
      }
      const query = createQuery([searchContext], [], traversals);
      const blobResponse = await connection.invoke(
        'queryView',
        view,
        query,
        refresh,
      );
      const response = await readBlob(blobResponse);
      if (response != null) {
        const isGeoContext = !!searchContext.geoType || searchContext.label === 'region';
        const items = getItems(
          response,
          searchContext.id,
          true,
          isGeoContext,
        );
        return items;
      }
    }
  }

  return null;
};

const confirmRelationshipQuery = async (connection, suggestion, relationshipType) => {
  if (!isConnectionReady(connection) || !suggestion) {
    return null;
  }

  const normalizedSignals = Array.isArray(suggestion.signals)
    ? suggestion.signals
        .map((signal) => (typeof signal === 'string' ? signal : signal?.type))
        .filter(Boolean)
    : [];

  if (!suggestion.personId1 || !suggestion.personId2) {
    return null;
  }

  const notesPayload = suggestion.details
    ? {
        ...suggestion.details,
        relationshipType,
      }
    : null;

  const request = {
    personId1: suggestion.personId1,
    personId2: suggestion.personId2,
    relationshipType: relationshipType,
    evidenceSignals: normalizedSignals,
    notes: notesPayload ? JSON.stringify(notesPayload) : null,
  };
  const newEdge = await connection.invoke('ConfirmRelationship', request);
  return newEdge;
};

const getRelationshipSuggestionsQuery = async (connection, personId, signals) => {
  if (isConnectionReady(connection)) {
    // signals is already processed from popup: { enabledSignals, minSharedEvents, radiusMiles, metroRegion }
    const request = {
      personId,
      enabledSignals: signals.enabledSignals,
      minSharedEvents: signals.minSharedEvents,
      radiusMiles: signals.radiusMiles,
      metroRegion: signals.metroRegion,
    };
    const response = await connection.invoke('GetRelationshipSuggestions', request);
    return response;
  }
  return null;
};

function* querySaga(action) {
  const { search, refresh } = action;
  yield put(setIsLoadingGraphItems(true));
  try {
    const connection = yield select(selectConnection);
    const response = yield call(query, connection, search, refresh);
    const cancel = yield select(selectCancel);

    if (response != null && response.nodes != null && !cancel) {
      const { nodes, links, nodeCount, blobUrl } = response;
      // Attach blob URL to search context for favorites persistence
      const searchWithBlob = { ...search, blobUrl };
      yield put(setGraphItem(searchWithBlob, nodes, links, nodeCount));
    } else {
      yield put(cancelSearch());
    }
  } catch (error) {
    console.error('querySaga failed', error);
    const errorMessage = getErrorMessage(error);
    yield put(setError({
      message: errorMessage,
      type: 'query_failed',
      timestamp: Date.now(),
    }));
    yield put(cancelSearch());
  } finally {
    yield put(setIsLoadingGraphItems(false));
  }
}

function* queryViewSaga(action) {
  const { view, context, refresh } = action;
  yield put(setIsLoadingGraphItems(true));
  try {
    const connection = yield select(selectConnection);
    const response = yield call(queryView, connection, view, context, refresh);
    if (response != null) {
      const { nodes, links, nodeCount } = response;
      if (nodes && nodes != null) {
        yield put(removeGraphItem(context.id));
        yield put(
          setGraphItem(
            { ...context, currentView: view },
            nodes,
            links,
            nodeCount,
          ),
        );
      } else {
        yield put(removeGraphItem(context.id));
        yield put(setGraphItem({ ...context, currentView: view }, {}, {}, 0));
      }
    } else {
      yield put(removeGraphItem(context.id));
      yield put(setGraphItem({ ...context, currentView: view }, {}, {}, 0));
    }
  } catch (error) {
    console.error('queryViewSaga failed', error);
    const errorMessage = getErrorMessage(error);
    yield put(setError({
      message: errorMessage,
      type: 'query_view_failed',
      timestamp: Date.now(),
    }));
    yield put(removeGraphItem(context.id));
    yield put(setGraphItem({ ...context, currentView: view }, {}, {}, 0));
  } finally {
    yield put(setIsLoadingGraphItems(false));
  }
}

const peerViewQuery = async (connection, lookIds, context) => {
  if (isConnectionReady(connection)) {
    if (lookIds && lookIds.length > 0) {
      const blobResponse = await connection.invoke('peerTopeerList', lookIds);
      const response = await readBlob(blobResponse);
      const items = getItems(response, context.id);
      return items;
    }
  }
  return null;
};

const exportGraphQuery = async (connection, contexts) => {
  if (isConnectionReady(connection) && contexts && contexts.length > 0) {
    let traversals = 3;
    if (contexts.length === 1 && contexts[0].id === '') {
      traversals = 2;
    }
    const query = createQuery(contexts, [], traversals);
    const csvData = await connection.invoke('exportGraph', query);
    return csvData;
  }
  return null;
};

// Helper function to download CSV
const downloadCsv = (csvData, filename = 'graph-export.csv') => {
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });

  // Edge/IE legacy path
  if (typeof window !== 'undefined' && window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename);
    return;
  }

  // Modern browsers — mirror the image-export pattern (no DOM append,
  // no visibility tricks) so the `download` attribute is reliably honored
  // even when the click occurs after an async round-trip.
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  link.click();

  // Revoke after a tick so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

function* peerViewQuerySaga({ lookIds }) {
  yield put(setIsLoadingGraphItems(true));
  const connection = yield select(selectConnection);
  const id = `p2p_${new Date().getTime()}`;
  const context = {
    id,
    displayValue: id,
    value: id,
  };
  const { nodes, links, nodeCount } = yield call(
    peerViewQuery,
    connection,
    lookIds,
    context,
  );
  const cancel = yield select(selectCancel);

  if (nodes != null && !cancel) {
    yield put(setGraphItem(context, nodes, links, nodeCount));
  } else {
    yield put(cancelSearch());
  }
  yield put(setIsLoadingGraphItems(false));
}

function* topConnectionsSaga(action) {
  const { payload } = action;
  yield put(setTopConnectionsLoading(true));
  const connection = yield select(selectConnection);
  const topConnections = yield call(topConnectionsQuery, connection, payload);
  if (topConnections?.length > 0) {
    yield put(setTopConnections(payload.id, topConnections));
  }
  yield put(setTopConnectionsLoading(false));
}

function* mostConnectedSaga(action) {
  const { payload } = action;
  yield put(setMostConnectedLoading(true));
  const connection = yield select(selectConnection);
  const mostConnected = yield call(mostConnectedQuery, connection, payload);
  if (mostConnected?.length > 0) {
    yield put(setMostConnected(payload.id, mostConnected));
  }
  yield put(setMostConnectedLoading(false));
}

function* resetViewSaga(action) {
  const { context } = action;
  const { currentView, ...contextWithoutView } = context;
  yield put(removeGraphItem(context.id));
  yield put(setSearch(contextWithoutView));
}

function* exportGraphSaga() {
  try {
    const connection = yield select(selectConnection);
    const contexts = yield select(selectContexts);

    if (!contexts || contexts.length === 0) {
      yield put(setError({
        message: 'No graph loaded to export. Search for a person or organization first.',
        type: 'export_failed',
        timestamp: Date.now(),
      }));
      return;
    }

    const csvData = yield call(exportGraphQuery, connection, contexts);
    if (csvData && csvData.length > 0) {
      downloadCsv(csvData);
    } else {
      console.warn('Export CSV: backend returned empty result');
      yield put(setError({
        message: 'No exportable connections were found for the current graph. Try reloading the target node and waiting for the graph to fully render before exporting.',
        type: 'export_failed',
        timestamp: Date.now(),
      }));
    }
  } catch (error) {
    console.error('Error exporting graph:', error);
    const errorMessage = getErrorMessage(error);
    yield put(setError({
      message: errorMessage,
      type: 'export_failed',
      timestamp: Date.now(),
    }));
  }
}

// Default organization shown on the exported report header/footer.
const EXPORT_ORGANIZATION_NAME = 'University of Illinois Foundation';

// Helper: invoke the backend Network Map Export and return the HTML document.
const exportNetworkMapQuery = async (connection, contexts, options) => {
  if (isConnectionReady(connection) && contexts && contexts.length > 0) {
    const traversals = Math.min(Math.max(options.degrees || 2, 1), 2);
    const query = createQuery(contexts, [], traversals);
    const html = await connection.invoke('exportNetworkMap', query, options);
    return html;
  }
  return null;
};

// Helper: open the returned HTML report in a new tab (user prints to PDF).
const openNetworkMapReport = (html) => {
  const reportWindow = window.open('', '_blank');
  if (!reportWindow) {
    return false;
  }
  reportWindow.document.open();
  reportWindow.document.write(html);
  reportWindow.document.close();
  reportWindow.focus();
  return true;
};

function* exportNetworkMapSaga() {
  try {
    yield put(setConfirmationResult({
      status: 'info',
      message: 'Generating Network Map report... This may take a few seconds.',
    }));

    const connection = yield select(selectConnection);
    const contexts = yield select(selectContexts);

    if (!contexts || contexts.length === 0) {
      yield put(setError({
        message: 'No graph loaded to export. Search for a person or organization first.',
        type: 'export_failed',
        timestamp: Date.now(),
      }));
      return;
    }

    const selectedVertexId = yield select(selectSelectedVertexId);
    const contextIds = yield select(selectContextIds);
    const degrees = yield select(selectDegreesOfSeparation);
    const user = yield select(selectUsername);

    // Resolve export target from the active context node, not focusedNode.
    // `focusedNode` is for viewport focus (e.g., filter cards) and can point
    // to a non-target node, which would incorrectly re-center the export.
    // Prefer an explicitly selected context node, then fall back to the most
    // recently added context id.
    const nodesDict = yield select(selectNodes);
    const selectedIsContextNode = Boolean(
      selectedVertexId && nodesDict?.[selectedVertexId]?.node?.isContextNode,
    );
    const contextIdKeys = Object.keys(contextIds || {});
    const latestContextId = contextIdKeys.length > 0
      ? contextIdKeys[contextIdKeys.length - 1]
      : contexts[contexts.length - 1]?.id || contexts[0]?.id;
    const targetNodeId = (selectedIsContextNode ? selectedVertexId : latestContextId) || '';

    const options = {
      targetNodeId,
      degrees: Math.min(Math.max(degrees || 2, 1), 2),
      activeCategories: [],
      exportedBy: user?.username || '',
      organizationName: EXPORT_ORGANIZATION_NAME,
      format: 'html',
    };

    const html = yield call(exportNetworkMapQuery, connection, contexts, options);
    if (html && html.length > 0) {
      const opened = openNetworkMapReport(html);
      if (!opened) {
        yield put(setConfirmationResult({
          status: 'error',
          message: 'Unable to open the export. Please allow pop-ups for this site and try again.',
        }));
        yield put(setError({
          message: 'Unable to open the export. Please allow pop-ups for this site and try again.',
          type: 'export_failed',
          timestamp: Date.now(),
        }));
      } else {
        yield put(setConfirmationResult({
          status: 'success',
          message: 'Network Map report is ready in a new tab.',
        }));
      }
    } else {
      console.warn('Network Map Export: backend returned empty result');
      yield put(setConfirmationResult({
        status: 'error',
        message: 'No exportable map was found for the current target.',
      }));
      yield put(setError({
        message: 'No exportable map was found for the current target. Try reloading the target node and waiting for the graph to fully render before exporting.',
        type: 'export_failed',
        timestamp: Date.now(),
      }));
    }
  } catch (error) {
    console.error('Error exporting network map:', error);
    const errorMessage = getErrorMessage(error);
    yield put(setConfirmationResult({
      status: 'error',
      message: errorMessage,
    }));
    yield put(setError({
      message: errorMessage,
      type: 'export_failed',
      timestamp: Date.now(),
    }));
  }
}

// Helper: Extract signal types from mixed array of strings/objects
const extractSignalTypes = (signals) => {
  if (!Array.isArray(signals)) return [];
  return signals
    .map((signal) => (typeof signal === 'string' ? signal : signal?.type))
    .filter(Boolean);
};

// Helper: Create snapshot data for person
const createSnapshotData = (backendProps) => ({
  total: currencyFormatter(backendProps.lifetimeGiving || 0),
  universityGiving: [
    { name: 'UIUC', amount: currencyFormatter(backendProps.uiucltg || 0) },
    { name: 'UIC', amount: currencyFormatter(backendProps.uicltg || 0) },
    { name: 'UIS', amount: currencyFormatter(backendProps.uisltg || 0) },
  ],
});

// Helper: Build person data object with all required fields
const buildPersonData = (backendProps, suggestion) => {
  const lifetimeGivingValue = backendProps.lifetimeGiving || 0;
  const snapshot = backendProps.snapshot || createSnapshotData(backendProps);

  return {
    ...backendProps,
    name: backendProps.name || suggestion.personName || 'Unknown',
    nodeKey: backendProps.nodeKey || suggestion.personId2,
    recordKey: backendProps.recordKey || backendProps.keyName || backendProps.keyname || suggestion.personId2,
    region: backendProps.region || suggestion.metroRegion || '',
    nodeType: backendProps.nodeType || nodeType.Person,
    age: backendProps.age || '',
    maiden: backendProps.maiden || '',
    prospectStatus: backendProps.prospectStatus || backendProps.rating || '',
    affliationUnit: backendProps.affliationUnit || backendProps.primaryaffiliation || '',
    lifetimeGiving: lifetimeGivingValue,
    imgSrc: backendProps.imgSrc || backendProps.blobUrl || '',
    tedUrl: backendProps.tedUrl || backendProps.crmurl || '',
    ingestionDateUtc: backendProps.ingestionDateUtc || null,
    primaryAddress: backendProps.primaryAddress || backendProps.primaryaddress || '',
    maritalStatus: backendProps.maritalStatus || backendProps.maritalstatus || '',
    nickname: backendProps.nickname || '',
    country: backendProps.country || '',
    title: backendProps.title || '',
    snapshot,
  };
};

function* getRelationshipSuggestionsSaga(action) {
  const { personId, signals } = action.payload;
  yield put(setRelationshipSuggestionsLoading(true));
  try {
    const connection = yield select(selectConnection);
    // Fetching relationship suggestions (log removed)
    const response = yield call(getRelationshipSuggestionsQuery, connection, personId, signals);

    // Early return if no suggestions
    if (!response?.suggestions?.length) {
      // No suggestions returned from backend for personId (log removed)
      yield put(setRelationshipSuggestions(personId, { suggestedNodes: {}, suggestedEdges: {} }));
      return;
    }

    const suggestedNodes = {};
    const suggestedEdges = {};
    // Processing suggestions (log removed)
    
    response.suggestions.forEach((s, index) => {
      // Validate required fields
      if (!s.personId1 || !s.personId2) {
        console.warn('[Suggestions] Skipping suggestion', index, '- missing personId1 or personId2:', s);
        return;
      }
      
      const signalList = Array.isArray(s.signals) ? s.signals : [];
      const signalTypes = extractSignalTypes(signalList);
      
      // Build edge
      const edgeKey = [s.personId1, s.personId2].sort().join('--');
      suggestedEdges[edgeKey] = {
        id1: s.personId1,
        id2: s.personId2,
        signals: signalTypes,
        confidence: s.combinedConfidence,
        metadata: signalList.reduce((acc, sig) => {
          if (sig?.type) {
            acc[sig.type] = sig.metadata;
          }
          return acc;
        }, {}),
      };
      
      // Build node (if new)
      if (!suggestedNodes[s.personId2]) {
        const backendProps = s.properties || {};
        const personData = buildPersonData(backendProps, s);
        
        suggestedNodes[s.personId2] = {
          id: s.personId2,
          label: 'person',
          value: s.personName || 'Unknown',
          signals: signalTypes,
          confidence: s.combinedConfidence,
          data: personData,
        };
      }
    });
    
    // Created suggested nodes/edges (log removed)
    yield put(setRelationshipSuggestions(personId, { suggestedNodes, suggestedEdges }));
  } catch (error) {
    console.error('[Suggestions] getRelationshipSuggestionsSaga failed:', error);
    const errorMessage = getErrorMessage(error);
    yield put(setError({
      message: errorMessage,
      type: 'suggestions_failed',
      timestamp: Date.now(),
    }));
  } finally {
    yield put(setRelationshipSuggestionsLoading(false));
  }
}

function* confirmRelationshipSaga(action) {
    const { suggestion, relationshipType } = action.payload;
    try {
        const connection = yield select(selectConnection);
        const newEdge = yield call(confirmRelationshipQuery, connection, suggestion, relationshipType);
        // Remove the confirmed suggestion from the suggestions panel
        yield put(dismissRelationshipSuggestion(suggestion.personId1, suggestion.personId2));
        yield put(setConfirmationResult({
            status: 'success',
            message: `Relationship confirmed: ${suggestion.personId1} ↔ ${suggestion.personId2} (${relationshipType})`,
            timestamp: Date.now(),
        }));
    } catch (error) {
        console.error('confirmRelationshipSaga failed', error);
        const errorMessage = getErrorMessage(error);
        yield put(setConfirmationResult({
            status: 'error',
            message: `Failed to confirm relationship: ${errorMessage}`,
            timestamp: Date.now(),
        }));
        yield put(setError({
            message: errorMessage,
            type: 'confirm_relationship_failed',
            timestamp: Date.now(),
        }));
    }
}

// Build a short human-readable label from filter values, e.g. "Microsoft, Board + Major Donor"
// Each filter contributes a compact summary of its values; the parts are joined with " + ".
// Individual value lists are capped at 2 items to keep the label readable.
const buildFilterLabel = (filters) => {
  const MAX_VALUES = 2; // max values shown per filter type
  const parts = (filters || []).map((f) => {
    const t = (f.Type || f.type || '').toLowerCase();
    const vals = f.FilterValue || f.filterValue;

    if (t === 'lifetime_giving') {
      const rangeFilters = f.Filters || f.filters || [];
      const min = rangeFilters[0]?.FilterValue ?? rangeFilters[0]?.filterValue;
      const max = rangeFilters[1]?.FilterValue ?? rangeFilters[1]?.filterValue;
      const fmt = (n) => {
        if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
        if (n >= 1000) return `${Math.round(n / 1000)}K`;
        return String(n);
      };
      return `$${fmt(min || 0)}–${fmt(max || 0)}`;
    }

    if (t === 'age') {
      const rangeFilters = f.Filters || f.filters || [];
      const start = rangeFilters[0]?.FilterValue ?? rangeFilters[0]?.filterValue ?? 0;
      const end = rangeFilters[1]?.FilterValue ?? rangeFilters[1]?.filterValue ?? 120;
      return `Age ${start}–${end}`;
    }

    if (Array.isArray(vals) && vals.length > 0) {
      const shown = vals.slice(0, MAX_VALUES).join(', ');
      const extra = vals.length > MAX_VALUES ? ` +${vals.length - MAX_VALUES}` : '';
      return `${shown}${extra}`;
    }

    // Fallback to type abbreviation if no values
    switch (t) {
      case 'affiliation': return 'Affil';
      case 'constituency': return 'Const';
      case 'region': return 'Region';
      case 'prospect': return 'Prospect';
      default: return t;
    }
  });
  return parts.filter(Boolean).join(' + ');
};

/**
 * Re-keys the filter_session hub node and its links from the backend's
 * deterministic ID to a unique context ID so multiple filter searches coexist.
 */
const remapHubNode = (items, oldHubId, newHubId, extraData) => {
  const hubNode = items.nodes[oldHubId];
  if (!hubNode) return;

  const hubNodeData = hubNode.data || hubNode.node?.data;
  const mergedData = { ...hubNodeData, nodeKey: newHubId, ...extraData };
  const remappedHub = { ...hubNode, id: newHubId, data: mergedData };

  if (hubNode.node) {
    remappedHub.node = { ...hubNode.node, id: newHubId, data: { ...hubNode.node.data, nodeKey: newHubId, ...extraData } };
  }

  delete items.nodes[oldHubId];
  items.nodes[newHubId] = remappedHub;

  // Re-key links that reference the old hub ID
  const oldLinks = { ...items.links };
  items.links = {};
  for (const linkId in oldLinks) {
    const link = oldLinks[linkId];
    const newLinkId = linkId.replace(oldHubId, newHubId);
    items.links[newLinkId] = {
      ...link,
      id1: link.id1 === oldHubId ? newHubId : link.id1,
      id2: link.id2 === oldHubId ? newHubId : link.id2,
    };
  }
};

function* filterOnlySearchSaga(action) {
  const filters = action.payload;
  if (!filters || filters.length < 2) {
    yield put(setError({
      message: 'Apply at least 2 filters to search',
      type: 'warning',
      timestamp: Date.now(),
    }));
    return;
  }
  yield put(setIsLoadingGraphItems(true));
  const maxResults = 1000;
  try {
    const connection = yield select(selectConnection);
    const blobUrl = yield call(
      [connection, connection.invoke],
      'filterOnlySearch',
      filters,
      maxResults,
    );
    const response = yield call(readBlob, blobUrl);
    if (response == null) {
      yield put(cancelSearch());
      return;
    }

    // Find the filter_session hub vertex in response
    const vertices = response.Vertices || response.vertices || {};
    const filterSessionVertexId = Object.keys(vertices).find(
      (key) => vertices[key].label === 'filter_session',
    );

    // Generate a unique context ID so multiple filter searches coexist
    const uniqueContextId = `${filterSessionVertexId}-${Date.now()}`;

    const items = getItems(response, filterSessionVertexId);
    if (items && items.nodes) {
      const resultCount = Math.max(items.nodeCount - 1, 0);
      const totalMatchCount = response?.totalMatchCount ?? response?.TotalMatchCount ?? resultCount;
      const displayedCount = resultCount;

      // Re-key the hub node so each filter search gets its own context entry
      if (filterSessionVertexId && items.nodes[filterSessionVertexId]) {
        remapHubNode(items, filterSessionVertexId, uniqueContextId, {
          totalMatchCount,
          maxLimit: maxResults,
          displayedCount,
        });
      }

      const displayValue = buildFilterLabel(filters);

      // Update the hub node's graph label to show filter values instead of generic "Filter Session"
      const hubNodeFinal = items.nodes[uniqueContextId];
      if (hubNodeFinal) {
        const truncatedLabel = displayValue.length > 30
          ? displayValue.substring(0, 27) + '...'
          : displayValue;
        hubNodeFinal.label = {
          ...hubNodeFinal.label,
          text: `🔍 ${truncatedLabel}`,
        };
      }

      const context = {
        id: uniqueContextId,
        displayValue,
        value: displayValue,
        label: 'filter_session',
        nodeCount: items.nodeCount,
        resultCount,
        displayedCount,
        totalMatchCount,
        maxLimit: maxResults,
        filters,
        blobUrl,
      };
      yield put(setGraphItem(context, items.nodes, items.links, items.nodeCount));
      // Clear map filters so all returned nodes are visible — the backend
      // already applied the filter criteria; keeping them active on the
      // frontend would double-filter and hide legitimate results.
      yield put(clearNodeFilters());
    } else {
      yield put(cancelSearch());
    }
  } catch (error) {
    console.error('filterOnlySearchSaga failed', error);
    const errorMessage = getErrorMessage(error);
    yield put(setError({
      message: errorMessage,
      type: 'filter_search_failed',
      timestamp: Date.now(),
    }));
    yield put(cancelSearch());
  } finally {
    yield put(setIsLoadingGraphItems(false));
  }
}

function* graphSaga() {
  yield takeLatest(SET_SEARCH_CONTEXT, querySaga);
  yield takeLatest(SET_VIEW, queryViewSaga);
  yield takeLatest(SET_RESET_VIEW, resetViewSaga);
  yield takeLatest(SET_PEER_VIEW, peerViewQuerySaga);
  yield takeLatest(GET_TOP_CONNECTIONS, topConnectionsSaga);
  yield takeLatest(GET_MOST_CONNECTED, mostConnectedSaga);
  yield takeLatest(SET_NL_MESSAGE, nlQuerySaga);
  yield takeLatest(EXPORT_GRAPH, exportGraphSaga);
  yield takeLatest(EXPORT_NETWORK_MAP, exportNetworkMapSaga);
  yield takeLatest(GET_RELATIONSHIP_SUGGESTIONS, getRelationshipSuggestionsSaga);
  yield takeLatest(CONFIRM_RELATIONSHIP_SUGGESTION, confirmRelationshipSaga);
  yield takeLatest(EXECUTE_FILTER_SEARCH, filterOnlySearchSaga);
}

export default graphSaga;
